import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import { Modal, Field } from "./Orders";
import { formatCurrency, formatDate } from "../lib/format";
import { Plus, TrashSimple, Eye, Receipt } from "@phosphor-icons/react";

const emptyItem = { description: "", quantity: 1, rate: 0, amount: 0 };

export default function Invoices() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();
    const { settings } = useSettings();

    const [form, setForm] = useState({
        client_name: "",
        client_mobile: "",
        client_address: "",
        client_gst: "",
        items: [{ ...emptyItem }],
        gst_percent: 18,
        discount: 0,
        paid: 0,
        notes: "",
        upi_id: "",
    });

    // Pre-fill UPI from company settings once they load
    useEffect(() => {
        if (settings.upi_id && !form.upi_id) {
            setForm((f) => ({ ...f, upi_id: settings.upi_id }));
        }
        // eslint-disable-next-line
    }, [settings.upi_id]);

    const load = () => {
        setLoading(true);
        api.get("/invoices")
            .then((r) => setInvoices(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const subtotal = form.items.reduce(
        (s, i) => s + Number(i.quantity || 0) * Number(i.rate || 0),
        0,
    );
    const gst_amount = (subtotal * Number(form.gst_percent || 0)) / 100;
    const total = subtotal + gst_amount - Number(form.discount || 0);
    const due = total - Number(form.paid || 0);

    const updateItem = (idx, key, val) => {
        const items = [...form.items];
        items[idx] = { ...items[idx], [key]: val };
        items[idx].amount =
            Number(items[idx].quantity || 0) * Number(items[idx].rate || 0);
        setForm({ ...form, items });
    };
    const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
    const removeItem = (idx) =>
        setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            items: form.items.map((i) => ({
                ...i,
                quantity: Number(i.quantity) || 0,
                rate: Number(i.rate) || 0,
                amount: Number(i.quantity || 0) * Number(i.rate || 0),
            })),
            gst_percent: Number(form.gst_percent) || 0,
            discount: Number(form.discount) || 0,
            paid: Number(form.paid) || 0,
            subtotal,
            gst_amount,
            total,
            due,
        };
        const { data } = await api.post("/invoices", payload);
        setShowModal(false);
        onResetForm();
        load();
        navigate(`/invoices/${data.id}`);
    };

    const onResetForm = () =>
        setForm({
            client_name: "",
            client_mobile: "",
            client_address: "",
            client_gst: "",
            items: [{ ...emptyItem }],
            gst_percent: 18,
            discount: 0,
            paid: 0,
            notes: "",
            upi_id: settings.upi_id || "",
        });

    const onDelete = async (id) => {
        if (!window.confirm("Delete this invoice?")) return;
        await api.delete(`/invoices/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="invoices-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="label-uppercase">Billing</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                        Invoices & Bills
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Professional PDF-ready bills with GST and UPI QR.
                    </p>
                </div>
                <button
                    data-testid="new-invoice-btn"
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-xl font-medium btn-glow"
                >
                    <Plus weight="bold" size={16} /> New Invoice
                </button>
            </div>

            <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="invoices-table">
                        <thead>
                            <tr className="text-left text-slate-400 border-b border-white/5">
                                <th className="px-5 py-3 label-uppercase">Invoice</th>
                                <th className="px-5 py-3 label-uppercase">Client</th>
                                <th className="px-5 py-3 label-uppercase">Date</th>
                                <th className="px-5 py-3 label-uppercase">Total</th>
                                <th className="px-5 py-3 label-uppercase">Due</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr
                                    key={inv.id}
                                    className="border-b border-white/5 hover:bg-white/[0.03]"
                                    data-testid={`invoice-row-${inv.id}`}
                                >
                                    <td className="px-5 py-4 font-mono text-xs text-slate-300">
                                        {inv.invoice_no}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="font-medium">{inv.client_name}</div>
                                        <div className="text-xs text-slate-500">
                                            {inv.client_mobile}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-slate-300">
                                        {formatDate(inv.issue_date)}
                                    </td>
                                    <td className="px-5 py-4 font-display font-semibold">
                                        {formatCurrency(inv.total)}
                                    </td>
                                    <td
                                        className={`px-5 py-4 font-display font-semibold ${
                                            inv.due > 0 ? "text-rose-300" : "text-emerald-300"
                                        }`}
                                    >
                                        {formatCurrency(inv.due)}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => navigate(`/invoices/${inv.id}`)}
                                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-blue-300"
                                                data-testid={`view-invoice-${inv.id}`}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => onDelete(inv.id)}
                                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400"
                                            >
                                                <TrashSimple size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && invoices.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-12 text-center text-slate-500">
                                        <Receipt size={28} className="mx-auto mb-2 opacity-50" />
                                        No invoices yet. Click "New Invoice" to create one.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <Modal title="New Invoice" onClose={() => setShowModal(false)}>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Client Name" required>
                                <input
                                    required
                                    className="input"
                                    value={form.client_name}
                                    onChange={(e) =>
                                        setForm({ ...form, client_name: e.target.value })
                                    }
                                    data-testid="invoice-client"
                                />
                            </Field>
                            <Field label="Mobile">
                                <input
                                    className="input"
                                    value={form.client_mobile}
                                    onChange={(e) =>
                                        setForm({ ...form, client_mobile: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="GSTIN">
                                <input
                                    className="input"
                                    value={form.client_gst}
                                    onChange={(e) =>
                                        setForm({ ...form, client_gst: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="UPI ID">
                                <input
                                    className="input"
                                    value={form.upi_id}
                                    onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                                />
                            </Field>
                            <Field label="Address">
                                <input
                                    className="input"
                                    value={form.client_address}
                                    onChange={(e) =>
                                        setForm({ ...form, client_address: e.target.value })
                                    }
                                />
                            </Field>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="label-uppercase">Items</span>
                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1"
                                    data-testid="add-item"
                                >
                                    <Plus size={12} /> Add row
                                </button>
                            </div>
                            <div className="rounded-xl border border-white/10 overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="text-slate-400 bg-white/[0.03]">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">
                                                Description
                                            </th>
                                            <th className="text-right px-3 py-2 font-medium w-20">
                                                Qty
                                            </th>
                                            <th className="text-right px-3 py-2 font-medium w-28">
                                                Rate
                                            </th>
                                            <th className="text-right px-3 py-2 font-medium w-28">
                                                Amount
                                            </th>
                                            <th className="px-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.items.map((it, idx) => (
                                            <tr key={idx} className="border-t border-white/5">
                                                <td className="px-2 py-1.5">
                                                    <input
                                                        className="input"
                                                        value={it.description}
                                                        onChange={(e) =>
                                                            updateItem(idx, "description", e.target.value)
                                                        }
                                                        placeholder="Service / item"
                                                        data-testid={`item-desc-${idx}`}
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input
                                                        type="number"
                                                        className="input text-right"
                                                        value={it.quantity}
                                                        onChange={(e) =>
                                                            updateItem(idx, "quantity", e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input
                                                        type="number"
                                                        className="input text-right"
                                                        value={it.rate}
                                                        onChange={(e) =>
                                                            updateItem(idx, "rate", e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-3 py-1.5 text-right font-display font-semibold">
                                                    {formatCurrency(
                                                        Number(it.quantity || 0) *
                                                            Number(it.rate || 0),
                                                    )}
                                                </td>
                                                <td className="px-2">
                                                    {form.items.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeItem(idx)}
                                                            className="p-1.5 text-slate-500 hover:text-rose-400"
                                                        >
                                                            <TrashSimple size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Field label="GST %">
                                <input
                                    type="number"
                                    className="input"
                                    value={form.gst_percent}
                                    onChange={(e) =>
                                        setForm({ ...form, gst_percent: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Discount (₹)">
                                <input
                                    type="number"
                                    className="input"
                                    value={form.discount}
                                    onChange={(e) =>
                                        setForm({ ...form, discount: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Paid (₹)">
                                <input
                                    type="number"
                                    className="input"
                                    value={form.paid}
                                    onChange={(e) => setForm({ ...form, paid: e.target.value })}
                                />
                            </Field>
                        </div>

                        <div className="glass p-4 rounded-xl grid grid-cols-2 gap-y-1 text-sm">
                            <span className="text-slate-400">Subtotal</span>
                            <span className="text-right">{formatCurrency(subtotal)}</span>
                            <span className="text-slate-400">
                                GST ({form.gst_percent || 0}%)
                            </span>
                            <span className="text-right">{formatCurrency(gst_amount)}</span>
                            <span className="text-slate-400">Discount</span>
                            <span className="text-right">−{formatCurrency(form.discount || 0)}</span>
                            <span className="font-display font-bold text-base mt-2">Total</span>
                            <span className="text-right font-display font-bold text-base mt-2">
                                {formatCurrency(total)}
                            </span>
                            <span className="text-emerald-300">Paid</span>
                            <span className="text-right text-emerald-300">
                                {formatCurrency(form.paid || 0)}
                            </span>
                            <span className="text-rose-300 font-semibold">Due</span>
                            <span className="text-right text-rose-300 font-semibold">
                                {formatCurrency(due)}
                            </span>
                        </div>

                        <Field label="Notes">
                            <textarea
                                rows={2}
                                className="input"
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            />
                        </Field>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="save-invoice"
                                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow font-medium text-sm"
                            >
                                Generate Invoice
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
